import { createContext } from 'react';
import {
	ICreateSubCategoryValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TFileId,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	ISubCategoryAllDishes,
	IUpdateSubCategory,
} from 'types';

export type TVisibleForm = 'mainForm' | 'translations';
export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';
export interface IInitialFormData {
	IDSelectedCustomer: Nullable<string>;
	IDsSelectedRestaurant: string[];
	nameOfSubCategory: string;
	IDSelectedCategory: Nullable<string>;
	IDsOfRestaurantsWhereToHide: string[];
	IDsOfRestaurantsAvailableForRepetition: string[];
	activationSchedule: string;
	deactivationSchedule: string;
	filePath: Nullable<string>;
	translations: IMenuTranslation[];
}

export interface IEditSubCategoryState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	isLoading: boolean;
	subCategoryID: string;
	visibleForm: TVisibleForm;
	filePath: Nullable<string>;
	preSignedUrl: string | null;
	fileId: TFileId;
	fetchedCustomers: IItemForGetShortInfo[];
	initialIDSelectedCustomer: Nullable<string>;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	initialIDsSelectedRestaurant: string[];
	IDsSelectedRestaurant: string[];
	nameOfSubCategory: string;
	fetchedCategories: IItemForGetShortInfo[];
	initialIDSelectedCategory: Nullable<string>;
	initialNameSelectedCategory: Nullable<string>; // If they make category selection active then remove that.
	IDSelectedCategory: Nullable<string>;
	initialIDsOfRestaurantsWhereToHide: string[];
	IDsOfRestaurantsWhereToHide: string[];
	initialIDsOfRestaurantsAvailableForRepetition: string[];
	IDsOfRestaurantsAvailableForRepetition: string[];
	dishesOfSubcategory: ISubCategoryAllDishes[];
	activationSchedule: string;
	deactivationSchedule: string;
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	dataForSubmit: IUpdateSubCategory | null;
	typeOfModalWindow: TTypeOfModalWindow;
	errorResponseData: IServerError<ICreateSubCategoryValidationError> | null;
}

export const editSubCategoryInitialState: IEditSubCategoryState = {
	needRefetchData: false,
	initialFormData: null,
	isLoading: true,
	subCategoryID: '',
	visibleForm: 'mainForm',
	filePath: null,
	preSignedUrl: null,
	fileId: null,
	fetchedCustomers: [],
	initialIDSelectedCustomer: null,
	IDSelectedCustomer: null,
	fetchedRestaurants: [],
	initialIDsSelectedRestaurant: [],
	IDsSelectedRestaurant: [],
	nameOfSubCategory: '',
	fetchedCategories: [],
	initialIDSelectedCategory: null,
	initialNameSelectedCategory: null, // If they make category selection active then remove that.
	IDSelectedCategory: null,
	initialIDsOfRestaurantsWhereToHide: [],
	IDsOfRestaurantsWhereToHide: [],
	initialIDsOfRestaurantsAvailableForRepetition: [],
	IDsOfRestaurantsAvailableForRepetition: [],
	activationSchedule: '',
	deactivationSchedule: '',
	dishesOfSubcategory: [],
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditSubCategoryContext extends IEditSubCategoryState {
	refetchData(value: boolean): void;
	changeLoaderStatus(value: boolean): void;
	changeVisibleForm(value: TVisibleForm): void;
	setFileId(value: TFileId): void;
	deletePhoto(): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: Nullable<string>): void;
	setFetchedRestaurants(value: IItemForGetShortInfoWithStatus[]): void;
	setIDsRestaurant(value: string[]): void;
	changeNameOfSubCategory(value: string): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setIDCategory(value: Nullable<string>): void;
	setIDsOfRestaurantsWhereToHide(value: string[]): void;
	setIDsOfRestaurantsAvailableForRepetition(value: string[]): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setDefaultCustomerLanguage(value: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	setDataForSubmit(value: IUpdateSubCategory): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	showErrorModalWithInfo(value: IServerError<ICreateSubCategoryValidationError> | null): void;
	resetFields(): void;
}

export const EditSubCategoryContext = createContext<IEditSubCategoryContext>({
	refetchData: () => undefined,
	changeLoaderStatus: () => undefined,
	changeVisibleForm: () => undefined,
	setFileId: () => undefined,
	deletePhoto: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setIDsRestaurant: () => undefined,
	changeNameOfSubCategory: () => undefined,
	setFetchedCategories: () => undefined,
	setIDCategory: () => undefined,
	setIDsOfRestaurantsWhereToHide: () => undefined,
	setIDsOfRestaurantsAvailableForRepetition: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setDefaultCustomerLanguage: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...editSubCategoryInitialState,
});
